import React, { useEffect, useRef, useState } from "react";

export default function InviteModal({ isOpen, onClose, onSend, organizationName, feedbackMessage }) {
  const [emailsInput, setEmailsInput] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [isOpen]);

  // Focus input & handle Escape key
  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();

    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  async function handleSend() {
    if (!emailsInput.trim()) {
      setErrorMessage("Please enter at least one email address.");
      inputRef.current?.focus();
      return;
    }

    const entries = emailsInput
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (entries.length === 0) {
      setErrorMessage("Please enter at least one email address.");
      inputRef.current?.focus();
      return;
    }

    try {
      setErrorMessage("");
      setSending(true);
      if (onSend) {
        await onSend(emailsInput);
      } else {
        // fallback demo delay
        await new Promise((r) => setTimeout(r, 600));
      }
      setSending(false);
      setEmailsInput("");
      onClose();
    } catch (err) {
      setSending(false);
      setErrorMessage(err?.response?.data?.message || err?.message || "Failed to send invite.");
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      aria-hidden={false}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* right-side panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title"
        className="fixed inset-y-0 right-0 flex items-start justify-end p-6 pointer-events-none"
      >
        <div
          className="pointer-events-auto w-full max-w-sm bg-white rounded-lg shadow-xl p-5 transform transition-transform translate-x-0"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            aria-label="Close invite dialog"
            onClick={onClose}
            className="absolute right-3 top-3 text-gray-600 hover:text-gray-800"
          >
            ✕
          </button>

          <h2
            id="invite-title"
            className="text-sm font-semibold text-gray-900"
          >
            To invite new members to <span className="font-bold">{organizationName || "your organization"}</span>,
            enter one or more recipient emails below
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Separate multiple emails with commas. We will send onboarding links to valid addresses.
          </p>

          <label className="block text-sm font-medium text-gray-700 mt-4">
            Email address(es)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={emailsInput}
            onChange={(e) => setEmailsInput(e.target.value)}
            placeholder="staff1@example.com, staff2@example.com"
            className="mt-2 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />

          {errorMessage ? <p className="mt-2 text-xs text-red-600">{errorMessage}</p> : null}
          {feedbackMessage ? <p className="mt-2 text-xs text-green-600">{feedbackMessage}</p> : null}

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className={`rounded-md px-3 py-2 text-sm text-white ${
                sending ? "bg-indigo-300" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {sending ? "Sending..." : "Send invite"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
